const parseHexToRgb = (input) => {
    if (!input || typeof input !== 'string') return null
    let hex = input.trim()
    if (hex.startsWith('#')) hex = hex.slice(1)
    if (hex.length === 3) {
        hex = hex
            .split('')
            .map((c) => c + c)
            .join('')
    }
    if (!/^[0-9a-fA-F]{6}$/.test(hex)) return null
    const n = parseInt(hex, 16)
    return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 }
}

const rgbToHsl = (r, g, b) => {
    const rn = r / 255
    const gn = g / 255
    const bn = b / 255
    const max = Math.max(rn, gn, bn)
    const min = Math.min(rn, gn, bn)
    const l = (max + min) / 2
    let h = 0
    let s = 0
    const d = max - min
    if (d !== 0) {
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
        switch (max) {
            case rn:
                h = ((gn - bn) / d + (gn < bn ? 6 : 0)) / 6
                break
            case gn:
                h = ((bn - rn) / d + 2) / 6
                break
            default:
                h = ((rn - gn) / d + 4) / 6
                break
        }
    }
    return { h: h * 360, s, l }
}

const hslToRgb = (hDeg, s, l) => {
    let h = ((hDeg % 360) + 360) % 360
    h /= 360
    let r
    let g
    let b
    if (s === 0) {
        r = g = b = l
    } else {
        const hue2rgb = (p, q, t) => {
            let tt = t
            if (tt < 0) tt += 1
            if (tt > 1) tt -= 1
            if (tt < 1 / 6) return p + (q - p) * 6 * tt
            if (tt < 1 / 2) return q
            if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6
            return p
        }
        const q = l < 0.5 ? l * (1 + s) : l + s - l * s
        const p = 2 * l - q
        r = hue2rgb(p, q, h + 1 / 3)
        g = hue2rgb(p, q, h)
        b = hue2rgb(p, q, h - 1 / 3)
    }
    return {
        r: Math.round(Math.min(255, Math.max(0, r * 255))),
        g: Math.round(Math.min(255, Math.max(0, g * 255))),
        b: Math.round(Math.min(255, Math.max(0, b * 255))),
    }
}

const rgbToHex = ({ r, g, b }) =>
    `#${[r, g, b]
        .map((x) => x.toString(16).padStart(2, '0'))
        .join('')}`

/**
 * Icon color in the same hue family as the tile: deep shade on light backgrounds
 * (e.g. #e39ef2 → ~#3f1f47), soft tint on dark backgrounds.
 * @param {string} backgroundHex
 * @returns {string} hex color for icon glyph
 */
export const harmonicIconToneForBackground = (backgroundHex) => {
    const rgb = parseHexToRgb(backgroundHex)
    if (!rgb) return '#3f1f47'
    const { h, s, l } = rgbToHsl(rgb.r, rgb.g, rgb.b)

    if (l > 0.42) {
        if (s < 0.06) {
            return '#3f3f46'
        }
        const iconL = Math.max(0.11, Math.min(0.26, -0.56 * l + 0.62))
        const iconS = Math.min(
            0.52,
            Math.max(0.28, s * (0.45 + 0.38 * (1 - l)))
        )
        return rgbToHex(hslToRgb(h, iconS, iconL))
    }

    if (s < 0.06) {
        return '#f4f4f5'
    }
    const iconL = Math.min(0.93, Math.max(0.82, l + 0.48 + (0.42 - l) * 0.12))
    const iconS = Math.min(0.42, Math.max(0.14, s * 0.52))
    return rgbToHex(hslToRgb(h, iconS, iconL))
}
